import { defineStore } from 'pinia'
import axios from 'axios'

// Ключи для localStorage
const CACHE_KEY = 'user_cache'

// Интервалы фоновых ретраев: 15с → 60с → 300с
const RETRY_DELAYS = [15000, 60000, 300000]

// Вспомогательная: сохранить кэш пользователя (все поля, кроме access_token)
function saveUserCache(user) {
  const cache = {}
  // Копируем все поля из user, кроме access_token
  for (const key of Object.keys(user)) {
    if (key !== 'access_token' && user[key] !== undefined && user[key] !== null) {
      cache[key] = user[key]
    }
  }
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
}

// Вспомогательная: очистить кэш пользователя
function clearUserCache() {
  localStorage.removeItem(CACHE_KEY)
}

// Вспомогательная: загрузить кэш пользователя
function loadUserCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export const useUserStore = defineStore({
  id: 'user',
  state: () => {
    const cached = loadUserCache()
    return {
      // Восстанавливаем все поля из кэша + access_token отдельно
      user: {
        access_token: localStorage.getItem('access_token') || null,
        ...cached
      },
      authChecked: false,
      showDialog: false,
      showServerErrorDialog: false,
      loading: false,
      your_error_code: false,
      serverUnavailable: false,
      // Для фонового ретрая
      _retryTimer: null,
      _retryAttempt: 0
    }
  },

  actions: {
    clearUser() {
      // Удаляем все поля из user, сохраняя только ссылку на объект
      for (const key of Object.keys(this.user)) {
        delete this.user[key]
      }
      // Восстанавливаем дефолтные значения
      this.user.access_token = null
      localStorage.removeItem('access_token');
      clearUserCache();
      this._stopRetry();
      this.serverUnavailable = false;
      this.authChecked = false;
    },

    openDialogNotAuth() {
      this.showDialog = true;
    },
    closeDialogNotAuth() {
      this.showDialog = false;
    },

    openServerErrorDialog() {
      this.showServerErrorDialog = true;
    },
    closeServerErrorDialog() {
      this.showServerErrorDialog = false;
    },

    // Остановить фоновый ретрай
    _stopRetry() {
      if (this._retryTimer) {
        clearTimeout(this._retryTimer)
        this._retryTimer = null
      }
      this._retryAttempt = 0
    },

    // Запустить фоновый ретрай авторизации
    _scheduleRetry() {
      this._stopRetry()
      if (this._retryAttempt >= RETRY_DELAYS.length) {
        return
      }
      const delay = RETRY_DELAYS[this._retryAttempt]
      this._retryAttempt++
      this._retryTimer = setTimeout(async () => {
        if (!this.user.access_token) {
          this._stopRetry()
          return
        }
        const result = await this.auth()
        if (result?.ServerError) {
          this._scheduleRetry()
        }
      }, delay)
    },

    async web_app(data) {
      try {
        const response = await axios.post('/api/web_app', data)
        const res = response.data
        this.user.access_token = res.access_token
        localStorage.setItem('access_token', res.access_token)
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
        return res.user
      } catch (error) {
        return error
      }
    },

    async email_register(data) {
      console.log('Sending registration data:', data)
      const response = await axios.post('/api/email_register', data)
      console.log('Registration response:', response.data)
      const res = response.data
      if (res.email_token) {
        this.user = Object.assign(this.user, data)
        this.user = Object.assign(this.user, res)
        console.log('Updated user state:', this.user)
      }
      return response
    },
    async new_token_email_register(data) {
      const response = await axios.post('/api/new_token_email_register', data)
      const res = response.data
      if (res.email_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },
    async code_verification_email_register(data) {
      const response = await axios.post('/api/code_verification_email_register', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user = Object.assign(this.user, res.user)
        this.user.access_token = res.access_token
        saveUserCache(this.user)
      }
      return response
    },

    async phone_register(data) {
      const response = await axios.post('/api/phone_register', data)
      const res = response.data
      if (res.phone_token) {
        this.user = Object.assign(this.user, data)
        this.user = Object.assign(this.user, res)
      }
      return response
    },
    async code_verification_phone_register(data) {
      const response = await axios.post('/api/code_verification_phone_register', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user = Object.assign(this.user, res.user)
        this.user.access_token = res.access_token
        saveUserCache(this.user)
      }
      return response
    },
    async new_token_phone_register(data) {
      const response = await axios.post('/api/new_token_phone_register', data)
      const res = response.data
      if (res.phone_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },
    async phone_reset_password(data) {
      const response = await axios.post('/api/phone_reset_password', data)
      const res = response.data
      if (res.phone_reset_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },
    async phone_reset_password_code_check(data) {
      const response = await axios.post('/api/phone_reset_password_code_check', data)
      const res = response.data
      if (res.new_password_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },

    async phone_create_new_password(data) {
      if (this.user.new_password_token == null) return
      axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.new_password_token
      const response = await axios.post('/api/phone_create_new_password', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
      }
      return response
    },

    async auth() {
      try {
        this.loading = true
        if (this.user.access_token == null) return
        axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.access_token
        const response = await axios.get('/api/user')
        const res = response.data
        if (res?.id) {
          this.authChecked = true;
        }
        if (res?.name) {
          this.user = Object.assign(this.user, res)
          saveUserCache(this.user)
          this.serverUnavailable = false
          this._stopRetry()
          return res
        }
        // Ответ без name — странная ситуация, но токен не трогаем
        return { Unauthenticated: true }
      } catch (e) {
        // 401 — токен реально невалиден/просрочен → чистим
        if (e.response && e.response.status === 401) {
          this.clearUser()
          return { Unauthenticated: true }
        }
        // Сетевые ошибки, 502/503, таймаут — токен сохраняем, юзер из кэша остаётся
        this.serverUnavailable = true
        this._scheduleRetry()
        return { ServerError: true }
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.access_token
        const response = await axios.get('/api/logout')
        const res = response.data
        if (res.logout) {
          this.clearUser()
        }
      } catch (e) {
        console.log(e)
      }
    },

    async login(data) {
      const response = await axios.post('/api/login', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
        console.log(this.user)
      }
      return response
    },
    async phone_login(data) {
      const response = await axios.post('/api/phone_login', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
      }
      return response
    },

    async email_reset_password_code_date(data) {
      this.user.email_reset_password_token = null
      const response = await axios.post('/api/email_reset_password_code_date', data)
      const res = response.data
      if (res.email_reset_password_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },
    async email_reset_password_code_check(data) {
      const response = await axios.post('/api/email_reset_password_code_check', data)
      const res = response.data
      if (res.new_password_token) {
        this.user = Object.assign(this.user, res)
      }
      return response
    },

    async email_create_new_password(data) {
      if (this.user.new_password_token == null) return
      axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.new_password_token
      const response = await axios.post('/api/email_create_new_password', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
      }
      return response
    },
    async discord_auth(data) {
      const response = await axios.post('/api/discord_auth', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
      }
      return response
    },
    async telegram_auth(data) {
      const response = await axios.post('/api/telegram_auth', data)
      const res = response.data
      if (res.access_token) {
        localStorage.setItem('access_token', res.access_token)
        this.user.access_token = res.access_token
        this.user = Object.assign(this.user, res.user)
        saveUserCache(this.user)
      }
      return response
    },
    your_error_code_status_true() {
      this.your_error_code = true
    },
    async get_error_user_code() {
      if (this.user.access_token == null) return
      axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.access_token
      const response = await axios.get('/api/get_error_user_code')
      if (response.data?.error_codes_count > 0) {
        this.your_error_code = true
      }
    },

    async update_browser_notifications(enabled) {
      if (this.user.access_token == null) return null
      axios.defaults.headers.common['Authorization'] = "Bearer " + this.user.access_token
      const response = await axios.post('/api/update_browser_notifications', {
        browser_notifications: enabled,
      })
      if (response.data?.success) {
        this.user.browser_notifications = response.data.browser_notifications
      }
      return response.data
    },
  }
})